import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
  class: "px-6 py-2"
};
const _hoisted_2 = {
  class: "w-1/2"
};
const _hoisted_3 = {
  key: 1
};
const _hoisted_4 = {
  class: "my-6"
};
const _hoisted_5 = {
  class: "py-2"
};
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import ListsService from '@/services/ListsService';
import RouteService from '@/services/RouteService';
import MainHeader from '@/components/MainHeader.vue';
import SettingsSaveBar from '@/components/SettingsSaveBar.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSelect from '@/components/LlSelect.vue';
import SnackbarComponent from '@/components/SnackbarComponent.vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
import { storeToRefs } from 'pinia';
export default {
  __name: 'UserInfo',
  props: {
    rootScope: {
      type: Object,
      required: true
    }
  },
  setup(__props) {
    const props = __props;

    // Initialize Pinia store
    const meStore = useMeStore();
    const {
      getMe,
      updateMe
    } = meStore;
    const {
      features,
      me,
      terms
    } = storeToRefs(meStore);
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();

    // Reactive state
    const saving = ref(false);
    const loading = ref(false);
    const form = reactive({
      first: '',
      last: '',
      initials: '',
      settings: {
        startState: null,
        ccPrimary: false,
        ccMeOnInvoicesByDefault: false
      }
    });
    const unsaved = ref(false);

    // Store initial form state for change detection
    const initialForm = reactive({});

    // Role Service
    const listsService = new ListsService();

    // Route Service
    let routeService = null;
    let deactivateRouteChange = null;

    // Computed properties
    const fullName = computed(() => {
      return me.value ? `${me.value.first} ${me.value.last}` : '';
    });
    const role = computed(() => {
      return listsService.userRoles.find(r => r.id === me.value?.role) || {
        id: null,
        label: '-'
      };
    });
    const roleLabel = computed(() => role.value.label);
    const responsible = computed(() => {
      return !['timekeeper', 'accountant', 'operator'].includes(role.value.id);
    });
    const states = computed(() => {
      const options = [{
        value: 'timeplanner',
        label: 'Calendar'
      }, {
        value: 'timeentries',
        label: 'Time Entries'
      }, {
        value: 'billing',
        label: 'Billing'
      }, {
        value: 'clients',
        label: 'Clients'
      }, {
        value: 'matters-next',
        label: `${terms.value?.Matter}s`
      }, {
        value: 'reports',
        label: `Reports`
      }];
      if (features.value.dashboard) {
        options.unshift({
          value: 'dashboard',
          label: 'Overview'
        });
      }
      if (features.value?.centralizedFixedFeePage) {
        // Insert Fixed Fees after Time Entries (index 2 when dashboard is present, index 1 otherwise)
        const billingIndex = options.findIndex(opt => opt.value === 'billing');
        options.splice(billingIndex, 0, {
          value: 'fixedfees',
          label: 'Fixed Fees'
        });
      }
      return options;
    });
    const somethingChanged = computed(() => {
      return JSON.stringify(form) !== JSON.stringify(initialForm);
    });

    // Methods
    const resetForm = () => {
      if (!me.value) return;
      const {
        settings
      } = me.value;
      const newForm = {
        first: me.value.first,
        last: me.value.last,
        initials: me.value.initials,
        settings: {
          startState: states.value.find(s => s.value === settings?.startState) || states.value[0],
          ccPrimary: settings?.ccPrimary || false,
          ccMeOnInvoicesByDefault: settings?.ccMeOnInvoicesByDefault || false
        }
      };
      Object.assign(form, newForm);
      Object.assign(initialForm, JSON.parse(JSON.stringify(newForm)));
    };
    const update = async () => {
      saving.value = true;
      try {
        const updatedForm = {
          id: me.value.id,
          ...form,
          settings: {
            ...form.settings,
            startState: form.settings.startState.value
          }
        };
        await updateMe(updatedForm);
        resetForm();
        saving.value = false;
        handleSuccess('Your settings were saved');
      } catch (err) {
        handleError(err);
        saving.value = false;
      }
    };
    const cancel = () => {
      Object.assign(form, JSON.parse(JSON.stringify(initialForm)));
    };

    // Lifecycle Hooks
    onMounted(async () => {
      loading.value = true;
      await getMe();
      loading.value = false;
      routeService = new RouteService(props);
      resetForm();
      const onRouteChangeStart = event => {
        if (somethingChanged.value) {
          unsaved.value = true;
          setTimeout(() => {
            unsaved.value = false;
          }, 4000);
          event.preventDefault();
        }
      };

      // Register route change listener
      deactivateRouteChange = routeService.rootScopeChange().$on('$stateChangeStart', onRouteChangeStart);
    });
    onBeforeUnmount(() => {
      if (deactivateRouteChange) {
        deactivateRouteChange();
      }
    });
    return (_ctx, _cache) => {
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(SnackbarComponent, {
        ref: "snackbar"
      }, null, 512), _createElementVNode("div", _hoisted_2, [loading.value ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
        key: 0,
        type: "list-item, list-item, list-item,button, button, list-item, list-item, list-item-three-line"
      })) : (_openBlock(), _createElementBlock("form", _hoisted_3, [_createVNode(MainHeader, {
        content: "My Info"
      }), _createVNode(LlTextField, {
        label: "First Name",
        modelValue: form.first,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => form.first = $event),
        hideDetails: false,
        rules: [v => !!v || 'First Name cannot be blank.']
      }, null, 8, ["modelValue", "rules"]), _createVNode(LlTextField, {
        label: "Last Name",
        modelValue: form.last,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => form.last = $event),
        hideDetails: false,
        rules: [v => !!v || 'Last Name cannot be blank.']
      }, null, 8, ["modelValue", "rules"]), _createVNode(LlTextField, {
        label: "Name/Initials for Invoicing",
        modelValue: form.initials,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => form.initials = $event),
        info: "By default, the user's full name is used on invoices. You can provide an alternative name for example the initials of the user.",
        placeholder: fullName.value
      }, null, 8, ["modelValue", "placeholder"]), _createElementVNode("div", _hoisted_4, [_cache[7] || (_cache[7] = _createElementVNode("label", {
        class: "block mb-2"
      }, "Invoice Notifications", -1)), responsible.value ? (_openBlock(), _createBlock(LlCheckbox, {
        key: 0,
        label: "CC me on invoices where I am responsible",
        modelValue: form.settings.ccPrimary,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => form.settings.ccPrimary = $event)
      }, null, 8, ["modelValue"])) : _createCommentVNode("", true), _createVNode(LlCheckbox, {
        label: "CC me on invoices I send by default",
        modelValue: form.settings.ccMeOnInvoicesByDefault,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => form.settings.ccMeOnInvoicesByDefault = $event)
      }, null, 8, ["modelValue"])]), _createVNode(LlTextField, {
        label: "Role",
        modelValue: roleLabel.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => roleLabel.value = $event),
        info: "This role is set by your LeanLaw administrator and can be changed in Firm Settings",
        readonly: "",
        hideDetails: false
      }, null, 8, ["modelValue"]), _createVNode(LlSelect, {
        modelValue: form.settings.startState,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => form.settings.startState = $event),
        items: states.value,
        "item-title": "label",
        "item-value": "value",
        density: "compact",
        "return-object": "",
        placeholder: "Starting Tab",
        variant: "outlined",
        "hide-details": "true",
        label: "Starting Tab",
        labelClass: "text-sm at-choose-client remove-outline",
        info: "This determines which tab you start out on when you log into LeanLaw",
        isAutocomplete: ""
      }, null, 8, ["modelValue", "items"]), _createElementVNode("div", _hoisted_5, [_cache[9] || (_cache[9] = _createElementVNode("label", null, "Login", -1)), _createElementVNode("p", null, [_cache[8] || (_cache[8] = _createTextVNode(" Your login email is ", -1)), _createElementVNode("b", null, _toDisplayString(_unref(me).email), 1)]), _cache[10] || (_cache[10] = _createElementVNode("p", null, [_createElementVNode("i", null, " To change your email or the way you login, please contact LeanLaw support by using the chat function. ")], -1))]), _createVNode(SettingsSaveBar, {
        somethingChanged: somethingChanged.value,
        saving: saving.value,
        unsaved: unsaved.value,
        onUpdate: update,
        onCancel: cancel
      }, null, 8, ["somethingChanged", "saving", "unsaved"])]))])]);
    };
  }
};